import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import TagManager from 'react-gtm-module';
import { connect } from 'react-redux';
import moment from 'moment';

import { AlbumFolderOnboardingFunnelData } from '../../../graphql/queries/album-folder';
import {
  albumFolderIsPrePaid,
  getAlbumFolderDiscountDescriptor,
  getAlbumFolderPaymentDescriptor,
} from '../../../util/album-folder';
import useThrottle from '../../../hooks/useThrottle';
import { UserAnalyticsFunnelData } from '../../../graphql/queries/account';
import useConsentManager from '../../../hooks/useConsentManager';
import { CONSENT_COOKIE_CATEGORIES } from '../../../constants/gdpr';

const propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  albumFolder: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  user: PropTypes.shape({
    id: PropTypes.string,
    album_memberships: PropTypes.shape({
      total: PropTypes.number,
    }),
  }),
};

const withCustomGTMDimensions = WrappedComponent => {
  const CustomGTMDimensions = ({ location, albumFolder, user, ...props }) => {
    const pathname = useThrottle(location.pathname, 3000);
    const { hasConsent } = useConsentManager();
    const hasNonEssentialConsent = hasConsent(
      CONSENT_COOKIE_CATEGORIES.NON_ESSENTIAL,
    );

    useEffect(() => {
      if (!albumFolder?.root || !user) {
        return;
      }

      if (!hasNonEssentialConsent) {
        return;
      }

      const [selectedIdentity] = user.selected_identities.records;

      TagManager.dataLayer({
        dataLayer: {
          event: 'newEvent',
          newEventData: { type: 'galleryTagging' },
          newSessionData: {
            galleryId: albumFolder.root.id,
            gallerySubType: albumFolder.root.album_subtype || 'GENERAL',
            galleryPaymentType: albumFolderIsPrePaid(albumFolder.root)
              ? 'Pre-paid'
              : 'Consumer Pay',
            galleryPaymentUnit: getAlbumFolderPaymentDescriptor(
              albumFolder.root,
            ),
            galleryPaymentDiscount: getAlbumFolderDiscountDescriptor(
              albumFolder.root,
            ),
            userHasMemberships: user.album_memberships.total > 0,
            userHasGalleryMembership:
              moment().diff(selectedIdentity?.created_at, 'hours') > 8,
          },
        },
      });
    }, [albumFolder?.id, user?.id, pathname, hasNonEssentialConsent]);

    return <WrappedComponent {...props} />;
  };

  CustomGTMDimensions.propTypes = propTypes;

  const withAlbumFolderData = graphql(AlbumFolderOnboardingFunnelData, {
    options: ({ match }) => ({
      variables: {
        id: match.params.albumFolderId,
      },
    }),
    props: ({ data: { albumFolder } }) => ({
      albumFolder,
    }),
    skip: ({ match, authenticated }) =>
      !match.params.albumFolderId || !authenticated,
  });

  const withAccountData = graphql(UserAnalyticsFunnelData, {
    options: ({ match }) => ({
      variables: {
        albumFolderId: match.params.albumFolderId,
      },
    }),
    props: ({ data }) => ({
      user: data?.me,
    }),
    skip: ({ match, authenticated }) =>
      !match.params.albumFolderId || !authenticated,
  });

  const mapStateToProps = ({ auth }) => ({
    authenticated: auth.authenticated,
  });

  return compose(
    withRouter,
    connect(mapStateToProps),
    withAlbumFolderData,
    withAccountData,
  )(CustomGTMDimensions);
};

export default withCustomGTMDimensions;
